import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { API_ENDPOINTS } from '../utils/config';
import { ArrowLeft } from 'lucide-react';
import EditProductModal from './EditProductModal';
import PreviewModal from './PreviewModal';
import RequestDetailsTab from './RequestDetailsTab';
import RequestAnalysisTab from './RequestAnalysisTab';
import RequestEmailsTab from './RequestEmailsTab';
import RequestApproveTab from './RequestApproveTab';
import './RequestDetailPage.css';

interface RequestDetailPageProps {}

const RequestDetailPage: React.FC<RequestDetailPageProps> = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t } = useTranslation();
  
  const [request, setRequest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'analysis' | 'approve' | 'emails'>('details');

  // Edit Product Modal state
  const [editProductModal, setEditProductModal] = useState({
    isOpen: false,
    selectedSuppliers: [] as any[]
  });

  // Preview Modal state
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    updatedSuppliers: [] as any[]
  });

  useEffect(() => {
    if (requestId && token) {
      fetchRequestDetails();
    }
  }, [requestId, token]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.REQUEST_DETAIL(requestId!), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setRequest(data.data);
          
          // Request data loaded successfully
          console.log('🔍 DEBUG - Request data:', data.data);
        } else {
          setError(data.message || 'Failed to load request details');
        }
      } else {
        setError('Failed to load request details');
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
      setError('Error loading request details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved': return '#10b981';
      case 'rejected': return '#ef4444';
      case 'pending': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const getPhaseColor = (phase: string) => {
    switch (phase?.toLowerCase()) {
      case 'completed': return '#10b981';
      case 'in_progress': return '#3b82f6';
      case 'pending': return '#f59e0b';
      default: return '#6b7280';
    }
  };














  // Edit Product Modal handlers
  const handleSaveChanges = (updatedSuppliers: any[]) => {
    console.log('🔍 SAVE CHANGES - Updated suppliers:', updatedSuppliers);
    // Update the edit modal state with the new data
    setEditProductModal(prev => ({
          ...prev,
      selectedSuppliers: updatedSuppliers
    }));
  };

  const handleContinueToPreview = (updatedSuppliers: any[]) => {
    console.log('🔍 DEBUG - RequestDetailPage - handleContinueToPreview called with:', updatedSuppliers);
    console.log('🔍 DEBUG - RequestDetailPage - updatedSuppliers.length:', updatedSuppliers?.length);
    console.log('🔍 DEBUG - RequestDetailPage - First supplier attached_documents:', updatedSuppliers?.[0]?.attached_documents);
    
    // Close edit modal and open preview modal
    setEditProductModal(prev => ({ ...prev, isOpen: false }));
    setPreviewModal({
      isOpen: true,
      updatedSuppliers: updatedSuppliers
    });
    
    console.log('🔍 DEBUG - RequestDetailPage - Preview modal state set to:', { isOpen: true, suppliersCount: updatedSuppliers?.length });
  };

  const handleBackToEdit = () => {
    // Close preview modal and open edit modal again
    setPreviewModal(prev => ({ ...prev, isOpen: false }));
    setEditProductModal({
      isOpen: true,
      selectedSuppliers: previewModal.updatedSuppliers
    });
  };

  // Approve request handler (for PreviewModal)
  const handleApproveRequest = async (updatedSuppliers: any[]) => {
    if (!token || !requestId) return;
    
    try {
      const requestBody = {
        selected_suppliers: updatedSuppliers
      };

      const response = await fetch(API_ENDPOINTS.APPROVE_REQUEST(requestId), {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          alert('Request approved successfully!');
          // Close all modals
          setPreviewModal({ isOpen: false, updatedSuppliers: [] });
          setEditProductModal({ isOpen: false, selectedSuppliers: [] });
          // Navigate back to admin dashboard
          navigate('/admin');
        } else {
          alert('Failed to approve request: ' + result.message);
        }
      } else {
        alert('Failed to approve request. Please try again.');
      }
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Error approving request. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="request-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="request-detail-page">
        <div className="error-container">
          <p className="error-message">{error || 'Request not found'}</p>
          <button onClick={() => navigate('/admin')} className="back-btn">
            {t('common.back_to_admin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="request-detail-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <button onClick={() => navigate('/admin')} className="back-btn">
            <ArrowLeft className="w-4 h-4" />
            {t('common.back_to_admin')}
          </button>
          <div className="header-title">
            <h1>{request.product_name}</h1>
            <div className="request-meta">
              <span className="request-id">ID: {request._id}</span>
              <span 
                className="status-badge"
                style={{ backgroundColor: getStatusColor(request.status) }}
              >
                {request.status || 'Pending'}
              </span>
              <span 
                className="phase-badge"
                style={{ backgroundColor: getPhaseColor(request.phase) }}
              >
                {request.phase || 'Phase 1'}
              </span>
            </div>
          </div>
        </div>
      </div>

            {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          {t('common.details')}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'analysis' ? 'active' : ''}`}
          onClick={() => setActiveTab('analysis')}
        >
          {t('common.supplier_analysis')}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'emails' ? 'active' : ''}`}
          onClick={() => setActiveTab('emails')}
        >
          {t('common.emails')}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'approve' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('approve');
          }}
        >
          {t('common.approve_request')}
        </button>

      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'details' && (
          <RequestDetailsTab 
            request={request} 
            formatDate={formatDate} 
          />
         )}

         {activeTab === 'analysis' && (
           <RequestAnalysisTab
             request={request}
             requestId={requestId!}
             formatDate={formatDate}
             onRequestUpdate={fetchRequestDetails}
           />
         )}

         {activeTab === 'emails' && (
           <RequestEmailsTab
             request={request}
             requestId={requestId!}
           />
         )}


         {activeTab === 'approve' && (
           <RequestApproveTab
             request={request}
             requestId={requestId!}
             onOpenEditModal={(selectedSuppliers) => {
               console.log('🔍 DEBUG - RequestDetailPage - Opening EditProductModal with suppliers:', selectedSuppliers);
               console.log('🔍 DEBUG - RequestDetailPage - Selected suppliers count:', selectedSuppliers.length);
               setEditProductModal({
                 isOpen: true,
                 selectedSuppliers: selectedSuppliers
               });
             }}
             onRequestUpdate={fetchRequestDetails}
           />
         )}

         {/* Edit Product Modal */}
         <EditProductModal
           isOpen={editProductModal.isOpen}
           onClose={() => setEditProductModal({ isOpen: false, selectedSuppliers: [] })}
           selectedSuppliers={editProductModal.selectedSuppliers}
           onSaveChanges={handleSaveChanges}
           onContinueToPreview={handleContinueToPreview}
         />

         {/* Preview Modal */}
         <PreviewModal
           isOpen={previewModal.isOpen}
           onClose={() => setPreviewModal({ isOpen: false, updatedSuppliers: [] })}
           updatedSuppliers={previewModal.updatedSuppliers}
           onApproveRequest={() => handleApproveRequest(previewModal.updatedSuppliers)}
           onBackToEdit={handleBackToEdit}
         />
       </div>
     </div>
   );
};

export default RequestDetailPage;

























